import tessraTokens from './tessra/tessra-tokens.json';
export const TESSRA_TOKENS = tessraTokens;
/** Widgets use Tessra's named colours, not the raw palette steps (`amber-500`). */
export const ALLOWED_TOKENS = {
    ...TESSRA_TOKENS,
    colors: TESSRA_TOKENS.colors.filter((name) => !/-\d{2,3}$/.test(name)),
};
export const VARIANTS = ['hover', 'focus-visible', 'dark', 'md'];
const COLOR_VARIANTS = ['hover', 'focus-visible', 'dark'];
const LAYOUT_VARIANTS = ['md'];
export const COMPONENT_CLASSES = [
    'card',
    'panel',
    'head',
    'heading',
    'title',
    'muted',
    'stack',
    'actions',
    'stat',
    'stat-label',
    'stat-value',
    'stat-delta',
    'stat-basis',
    'up',
    'down',
    'down-is-good',
    'neutral',
    'alert',
    'badge',
    'ok',
    'warn',
    'bad',
    'blue',
    'purple',
    'pink',
    'orange',
    'table',
    'num',
    'col-secondary',
    'list',
    'tabs',
    'callout',
    'progress',
    'btn',
    'ghost',
    'grid-2',
    'grid-3',
    'grid-4',
];
export const UTILITY_FAMILIES = [
    {
        prefix: 'bg',
        tokens: 'colors',
        variable: '--color-',
        properties: ['background-color'],
        variants: COLOR_VARIANTS,
    },
    {
        prefix: 'text',
        tokens: 'colors',
        variable: '--color-',
        properties: ['color'],
        variants: COLOR_VARIANTS,
    },
    {
        prefix: 'border',
        tokens: 'colors',
        variable: '--color-',
        properties: ['border-color'],
        variants: COLOR_VARIANTS,
    },
    { prefix: 'text', tokens: 'text', variable: '--text-', properties: ['font-size'], variants: [] },
    {
        prefix: 'font',
        tokens: 'fontWeight',
        variable: '--font-weight-',
        properties: ['font-weight'],
        variants: [],
    },
    {
        prefix: 'rounded',
        tokens: 'radius',
        variable: '--radius-',
        properties: ['border-radius'],
        variants: [],
    },
    {
        prefix: 'gap',
        tokens: 'space',
        variable: '--space-',
        properties: ['gap'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'p',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'px',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-left', 'padding-right'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'py',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-top', 'padding-bottom'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'pt',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-top'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'pb',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-bottom'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'pl',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-left'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'pr',
        tokens: 'space',
        variable: '--space-',
        properties: ['padding-right'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'm',
        tokens: 'space',
        variable: '--space-',
        properties: ['margin'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'mx',
        tokens: 'space',
        variable: '--space-',
        properties: ['margin-left', 'margin-right'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'my',
        tokens: 'space',
        variable: '--space-',
        properties: ['margin-top', 'margin-bottom'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'mt',
        tokens: 'space',
        variable: '--space-',
        properties: ['margin-top'],
        variants: LAYOUT_VARIANTS,
    },
    {
        prefix: 'mb',
        tokens: 'space',
        variable: '--space-',
        properties: ['margin-bottom'],
        variants: LAYOUT_VARIANTS,
    },
];
/** Layout utilities with fixed CSS, the same names Tailwind uses. */
export const LAYOUT_UTILITIES = {
    flex: 'display:flex',
    'inline-flex': 'display:inline-flex',
    grid: 'display:grid',
    block: 'display:block',
    'inline-block': 'display:inline-block',
    hidden: 'display:none',
    'flex-col': 'flex-direction:column',
    'flex-row': 'flex-direction:row',
    'flex-wrap': 'flex-wrap:wrap',
    'flex-1': 'flex:1 1 0%',
    'shrink-0': 'flex-shrink:0',
    grow: 'flex-grow:1',
    'items-start': 'align-items:flex-start',
    'items-center': 'align-items:center',
    'items-end': 'align-items:flex-end',
    'items-baseline': 'align-items:baseline',
    'justify-start': 'justify-content:flex-start',
    'justify-center': 'justify-content:center',
    'justify-end': 'justify-content:flex-end',
    'justify-between': 'justify-content:space-between',
    'grid-cols-1': 'grid-template-columns:repeat(1,minmax(0,1fr))',
    'grid-cols-2': 'grid-template-columns:repeat(2,minmax(0,1fr))',
    'grid-cols-3': 'grid-template-columns:repeat(3,minmax(0,1fr))',
    'grid-cols-4': 'grid-template-columns:repeat(4,minmax(0,1fr))',
    'col-span-2': 'grid-column:span 2/span 2',
    'col-span-3': 'grid-column:span 3/span 3',
    'col-span-4': 'grid-column:span 4/span 4',
    'w-full': 'width:100%',
    'h-full': 'height:100%',
    'min-w-0': 'min-width:0',
    truncate: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
    'whitespace-nowrap': 'white-space:nowrap',
    'text-left': 'text-align:left',
    'text-center': 'text-align:center',
    'text-right': 'text-align:right',
    uppercase: 'text-transform:uppercase',
    underline: 'text-decoration-line:underline',
    'tabular-nums': 'font-variant-numeric:tabular-nums',
    'overflow-hidden': 'overflow:hidden',
    'overflow-x-auto': 'overflow-x:auto',
    relative: 'position:relative',
    border: 'border-width:1px;border-style:solid',
    'border-t': 'border-top-width:1px;border-top-style:solid',
    'border-b': 'border-bottom-width:1px;border-bottom-style:solid',
    'sr-only': 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0',
};
function tokenFamilies(name) {
    return UTILITY_FAMILIES.filter((family) => name.startsWith(`${family.prefix}-`) &&
        ALLOWED_TOKENS[family.tokens].includes(name.slice(family.prefix.length + 1)));
}
const COMPONENTS = new Set(COMPONENT_CLASSES);
export function isAllowedClass(name) {
    const separator = name.indexOf(':');
    if (separator === -1) {
        return COMPONENTS.has(name) || name in LAYOUT_UTILITIES || tokenFamilies(name).length > 0;
    }
    const variant = name.slice(0, separator);
    const base = name.slice(separator + 1);
    if (base in LAYOUT_UTILITIES)
        return LAYOUT_VARIANTS.includes(variant);
    return tokenFamilies(base).some((family) => family.variants.includes(variant));
}
const CLASS_ATTRIBUTE = /class\s*=\s*"([^"]*)"/g;
/** Classes in the document that are not allowed, with the line each appears on. */
export function checkClasses(document) {
    const problems = [];
    for (const match of document.matchAll(CLASS_ATTRIBUTE)) {
        const line = document.slice(0, match.index).split('\n').length;
        const names = match[1]
            .replace(/\{\{[^}]*\}\}/g, ' ')
            .split(/\s+/)
            .filter(Boolean);
        for (const className of names)
            if (!isAllowedClass(className))
                problems.push({ line, className });
    }
    return problems;
}
